import React from 'react';

import FormattedMessage from 'components/formatted-message';
import AnalyticsIcon from 'components/svg-icons/analytics-icon';
import StatisticsIcon from 'components/svg-icons/statistics-icon';
import CalendarIcon from 'components/svg-icons/calendar-icon';
import PlayersIcon from 'components/svg-icons/players-icon';
import { LITE, PREMIUM, PREMIUM_PLUS, PRO } from 'containers/product-initializer/products';
import {
    analyticsIndex,
    analyticsPages,
    cloudVideoCreator,
    connectPage,
    conservatorIndex,
    eventsHomePage,
    explorePage,
    homePage,
    playerHomePage,
    preScoutPages,
    rawVideoPage,
    scheduleIndex,
    scoutIndex,
    scoutPages,
    statisticsIndex,
    statisticsPages,
    playerComparisonPage,
    goalieComparisonPage,
    teamComparisonPage,
    teamHomePage,
    teamLanderIndex
} from 'constants/page-names';
import { moduleMasks } from 'utils/permissions/module-masks';

const imgStyle = { width: '16px' };

const homeLogoStyle = {
    flex: 'none',
    height: '35px'
};

export const statisticsLinks = {
    shooting: {
        children: <FormattedMessage id="statistics.shooting" />,
        to: `/${statisticsIndex}/${statisticsPages.shooting}`
    },
    goaltenders: {
        children: <FormattedMessage id="statistics.goalkeepers" />,
        to: `/${statisticsIndex}/${statisticsPages.goaltenders}`,
        moduleMask: moduleMasks.GOALTENDERS
    },
    passing: {
        children: <FormattedMessage id="statistics.passing" />,
        to: `/${statisticsIndex}/${statisticsPages.passing}`,
        moduleMask: moduleMasks.PASSING
    },
    entries: {
        children: <FormattedMessage id="statistics.entries" />,
        to: `/${statisticsIndex}/${statisticsPages.entries}`,
        moduleMask: moduleMasks.ENTRIES
    },
    exits: {
        children: <FormattedMessage id="statistics.exits" />,
        to: `/${statisticsIndex}/${statisticsPages.exits}`,
        moduleMask: moduleMasks.EXITS
    },
    possession: {
        children: <FormattedMessage id="statistics.possession" />,
        to: `/${statisticsIndex}/${statisticsPages.possession}`,
        moduleMask: moduleMasks.POSSESSION
    },
    skating: {
        children: <FormattedMessage id="statistics.skating" />,
        to: `/${statisticsIndex}/${statisticsPages.skating}`,
        products: [PRO]
    },
    shifts: {
        children: <FormattedMessage id="statistics.shifts" />,
        to: `/${statisticsIndex}/${statisticsPages.shifts}`,
        moduleMask: moduleMasks.SHIFTS
    },
    playerComparison: {
        children: <FormattedMessage id="statistics.playerComparison" />,
        to: `/${playerComparisonPage}`,
        moduleMask: moduleMasks.PCT,
        products: [PRO, PREMIUM_PLUS, PREMIUM, LITE]
    },
    goalieComparison: {
        children: <FormattedMessage id="statistics.goalieComparison" />,
        to: `/${goalieComparisonPage}`,
        moduleMask: moduleMasks.PCT,
        products: [PRO, PREMIUM_PLUS, PREMIUM, LITE]
    }
};

export const analyticLinks = {
    summary: {
        children: <FormattedMessage id="dashboard.summary" />,
        to: `/${analyticsIndex}/${analyticsPages.summary}`
    },
    skaters: {
        children: <FormattedMessage id="dashboard.skaters" />,
        to: `/${analyticsIndex}/${analyticsPages.skaters}`
    },
    powerPlayer: {
        children: <FormattedMessage id="dashboard.powerPlay" />,
        to: `/${analyticsIndex}/${analyticsPages.powerPlay}`,
        products: [PRO, LITE]
    },
    penaltyKill: {
        children: <FormattedMessage id="dashboard.penaltyKill" />,
        to: `/${analyticsIndex}/${analyticsPages.penaltyKill}`,
        products: [PRO, LITE]
    },
    playersPage: {
        children: <FormattedMessage id="dashboard.playersPage" />,
        to: `/${analyticsIndex}/${analyticsPages.playersPage}`,
        moduleMask: moduleMasks.IPP
    },
    gameScore: {
        children: <FormattedMessage id="dashboard.gameScore" />,
        to: `/${analyticsIndex}/${analyticsPages.gameScore}`,
        moduleMask: moduleMasks.GAME_SCORE,
        products: []
    },
    configurator: {
        children: <FormattedMessage id="dashboard.configurator" />,
        to: `/${analyticsIndex}/${analyticsPages.configurator}`
    }
};

export const createHomeLink = path => ({
    children: <FormattedMessage id="home" />,
    to: `${path}/${homePage}`
});

export const createPlayerHomeLink = path => ({
    children: <FormattedMessage id="players" />,
    to: `${path}/${playerHomePage}`
});

export const createTeamHomeLink = path => ({
    children: <FormattedMessage id="teams" />,
    to: `${path}/${teamHomePage}`
});

export const createEventsHomeLink = path => ({
    children: <FormattedMessage id="events" />,
    to: `${path}/${eventsHomePage}`
});

const truePredicate = () => true;

export const createAnalyticLink = (submenu, path, linksPredicate = truePredicate) => {
    const filteredSubmenu = submenu.filter(linksPredicate);
    if (filteredSubmenu.length === 1) {
        return {
            children: filteredSubmenu[0].children,
            to: `${path}${filteredSubmenu[0].to}`,
            // eslint-disable-next-line tpe/no-inline-style -- TODO: legacy inline style (baseline)
            icon: <AnalyticsIcon style={imgStyle} />
        };
    } else {
        return {
            children: <FormattedMessage id="dashboard" />,
            to: `${path}/${analyticsIndex}`,
            // eslint-disable-next-line tpe/no-inline-style -- TODO: legacy inline style (baseline)
            icon: <AnalyticsIcon style={imgStyle} />,
            submenu: submenu.filter(linksPredicate).map(link => ({ ...link, to: `${path}${link.to}` }))
        };
    }
};

export const createStatisticsLink = (submenu, path, linksPredicate = truePredicate) => {
    const filteredSubmenu = submenu.filter(linksPredicate);
    if (filteredSubmenu.length === 1) {
        return {
            children: filteredSubmenu[0].children,
            to: `${path}${filteredSubmenu[0].to}`,
            // eslint-disable-next-line tpe/no-inline-style -- TODO: legacy inline style (baseline)
            icon: <StatisticsIcon style={imgStyle} />
        };
    } else {
        return {
            children: <FormattedMessage id="dashboard.statistics" />,
            to: `${path}/${statisticsIndex}`,
            // eslint-disable-next-line tpe/no-inline-style -- TODO: legacy inline style (baseline)
            icon: <StatisticsIcon style={imgStyle} />,
            submenu: filteredSubmenu.map(link => ({ ...link, to: `${path}${link.to}` }))
        };
    }
};

export const createConservatorLink = path => ({
    children: <FormattedMessage id="dashboard.conservator" />,
    to: `${path}/${conservatorIndex}`
});

export const createRawVideoLink = path => ({
    children: <FormattedMessage id="dashboard.raw" />,
    to: `${path}/${rawVideoPage}`
});

export const createTeamLanderLink = path => ({
    children: <FormattedMessage id="dashboard.team" />,
    to: `${path}/${teamLanderIndex}`
});

export const createScheduleLink = path => ({
    children: <FormattedMessage id="dashboard.schedule" />,
    to: `${path}/${scheduleIndex}`,
    // eslint-disable-next-line tpe/no-inline-style -- TODO: legacy inline style (baseline)
    icon: <CalendarIcon style={imgStyle} />
});

export const createPlayerComparisonLink = path => ({
    children: <FormattedMessage id="statistics.playerComparison" />,
    to: `${path}/${playerComparisonPage}`,
    // eslint-disable-next-line tpe/no-inline-style -- TODO: legacy inline style (baseline)
    icon: <PlayersIcon style={imgStyle} />,
    moduleMask: moduleMasks.PCT
});

export const createTeamComparisonLink = path => ({
    children: <FormattedMessage id="statistics.teamComparison" />,
    to: `${path}/${teamComparisonPage}`,
    // eslint-disable-next-line tpe/no-inline-style -- TODO: legacy inline style (baseline)
    icon: <PlayersIcon style={imgStyle} />,
    moduleMask: moduleMasks.PCT
});

export const createPreScoutLink = (submenu, path, linksPredicate = truePredicate) => ({
    children: <FormattedMessage id="dashboard.prescout" />,
    to: path,
    // eslint-disable-next-line tpe/no-inline-style -- TODO: legacy inline style (baseline)
    icon: <AnalyticsIcon style={imgStyle} />,
    submenu: submenu.filter(linksPredicate).map(link => ({ ...link, to: `${path}${link.to}` }))
});

export const createSuperLiteLink = (submenu, path, linksPredicate = truePredicate) => {
    return {
        children: <FormattedMessage id="dashboard.superLite" />,
        to: `${path}/dashboard`,
        // eslint-disable-next-line tpe/no-inline-style -- TODO: legacy inline style (baseline)
        icon: <AnalyticsIcon style={imgStyle} />,
        submenu: submenu.filter(linksPredicate).map(link => ({ ...link, to: `${path}/dashboard${link.to}` }))
    };
};

export const createTeamSelectLink = path => ({
    children: <FormattedMessage id="dashboard.scout.teamSelect" />,
    to: `${path}/${scoutPages.teams}`,
    // eslint-disable-next-line tpe/no-inline-style -- TODO: legacy inline style (baseline)
    icon: <AnalyticsIcon style={imgStyle} />
});

export const createPlayerSelectLink = path => ({
    children: <FormattedMessage id="dashboard.players" />,
    to: `${path}/${scoutPages.players}`,
    // eslint-disable-next-line tpe/no-inline-style -- TODO: legacy inline style (baseline)
    icon: <PlayersIcon style={homeLogoStyle} />
});

export const createFavoritesLink = (path, count) => ({
    children: (
        <span>
            <FormattedMessage id="dashboard.favorites" /> <span>{count !== null ? `(${count})` : ''}</span>
        </span>
    ),
    to: `${path}/${scoutPages.favorites}`,
    // eslint-disable-next-line tpe/no-inline-style -- TODO: legacy inline style (baseline)
    icon: <PlayersIcon style={homeLogoStyle} />
});

export const createReportLink = path => ({
    children: <FormattedMessage id="dashboard.reports" />,
    to: `${path}/${scoutPages.reports}`
});

export const createPlaylistsLink = path => ({
    children: <FormattedMessage id="dashboard.playlists" />,
    to: `${path}/${scoutPages.playlists}`
});

export const createScoutScheduleLink = path => ({
    children: <FormattedMessage id="dashboard.schedule" />,
    to: `${path}/${scoutPages.playerSchedule}`
});

export const createPlayersComparisonLink = path => ({
    children: <FormattedMessage id="dashboard.playersComparison" />,
    to: `${path}/${scoutPages.playerComparison}`,
    // eslint-disable-next-line tpe/no-inline-style -- TODO: legacy inline style (baseline)
    icon: <StatisticsIcon style={homeLogoStyle} />
});

export const createScoutLink = (submenu, path, linksPredicate = truePredicate) => ({
    children: <FormattedMessage id="dashboard.scout" />,
    to: `${path}/${scoutIndex}`,
    // eslint-disable-next-line tpe/no-inline-style -- TODO: legacy inline style (baseline)
    icon: <AnalyticsIcon style={imgStyle} />,
    submenu: submenu.filter(linksPredicate).map(link => ({ ...link, to: `${path}${link.to}` }))
});

export const preScoutLinks = {
    summary: {
        children: <FormattedMessage id="dashboard.summary" />,
        to: `/${preScoutPages.summary}`
    },
    goaltenders: {
        children: <FormattedMessage id="statistics.goalkeepers" />,
        to: `/${preScoutPages.goaltenders}`,
        moduleMask: moduleMasks.GOALTENDERS
    },
    shifts: {
        children: <FormattedMessage id="statistics.shifts" />,
        to: `/${preScoutPages.shifts}`,
        moduleMask: moduleMasks.SHIFTS
    }
};

// DEPRECATED (NFT): not used, the NFT video editor has no route. TODO: remove with src/pages/game-center/nft.
export const createVideoCloudCreateLink = path => ({
    children: <FormattedMessage id="cloudVideoCreator" />,
    to: `${path}/${cloudVideoCreator}`
});

export const createConnectLink = path => ({
    children: <FormattedMessage id="connectPage.page" />,
    to: `${path}/${connectPage}`
});

export const createExploreLink = path => ({
    children: <FormattedMessage id="explore.menu" />,
    to: `${path}/${explorePage}`
});
